import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import CharityAuth from '../components/charity/CharityAuth';
import CharityDashboard from '../components/charity/CharityDashboard';
import CharityPaymentSetup from '../components/charity/CharityPaymentSetup';
import CharityDonations from '../components/charity/CharityDonations';
import CharityProfile from '../components/charity/CharityProfile';
import CharityApplication from '../components/charity/CharityApplication';
// Help Center
import HelpPage from '../pages/HelpPage';
import { FloatingHelpButton } from '../components/shared/FloatingHelpButton';
import BuildInfo from '../components/BuildInfo';

function CharityAppContent() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [charity, setCharity] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if current path is the public application page
  const isPublicRoute = location.pathname === '/charity/apply';
  const isHelpRoute = location.pathname === '/charity/help';

  // Allow unauthenticated access to help page
  if (isHelpRoute) {
    return (
      <>
        <Routes>
          <Route path="/help" element={<HelpPage />} />
        </Routes>
        <FloatingHelpButton userRole="charity" />
      </>
    );
  }

  useEffect(() => {
    // Skip auth check for public routes
    if (isPublicRoute) {
      setLoading(false);
      return;
    }

    // Check for magic link token in URL
    const token = searchParams.get('token');
    if (token) {
      verifyMagicLink(token);
    } else {
      // Check for existing session
      const sessionToken = localStorage.getItem('charity_session_token');
      const savedCharity = localStorage.getItem('charity_data');
      if (sessionToken && savedCharity) {
        setCharity(JSON.parse(savedCharity));
      }
      setLoading(false);
    }
  }, [searchParams, isPublicRoute]);

  const verifyMagicLink = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/charity-portal/auth/verify-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('charity_session_token', data.session_token);
        localStorage.setItem('charity_data', JSON.stringify(data.charity));
        setCharity(data.charity);
        // Remove token from URL
        window.history.replaceState({}, '', '/charity');
      }
    } catch (error) {
      console.error('Failed to verify magic link:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (sessionToken, charityData) => {
    localStorage.setItem('charity_session_token', sessionToken);
    localStorage.setItem('charity_data', JSON.stringify(charityData));
    setCharity(charityData);
  };

  const handleLogout = () => {
    localStorage.removeItem('charity_session_token');
    localStorage.removeItem('charity_data');
    setCharity(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Public routes (no auth required)
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/apply" element={<CharityApplication />} />
      </Routes>
    );
  }

  // Auth required routes
  if (!charity) {
    return <CharityAuth onLogin={handleLogin} />;
  }

  return (
    <>
      <Routes>
        <Route path="/apply" element={<CharityApplication />} />
        <Route path="/" element={<CharityDashboard charity={charity} onLogout={handleLogout} />} />
        <Route path="/payment" element={<CharityPaymentSetup charity={charity} onLogout={handleLogout} />} />
        <Route path="/donations" element={<CharityDonations charity={charity} onLogout={handleLogout} />} />
        <Route path="/profile" element={<CharityProfile charity={charity} onLogout={handleLogout} />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="*" element={<Navigate to="/charity" replace />} />
      </Routes>
      <FloatingHelpButton userRole="charity" />
    </>
  );
}

export default function CharityApp() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <CharityAppContent />
      <BuildInfo />
    </>
  );
}
